/* eslint-disable prefer-destructuring */
import { PROVEN_NO, TYPE_A, UNPUBLISHED } from "const"
import {
    QidProduct,
    QidModel,
    QidModelSafetyFunction,
    QidModelOption,
    QidDatapoint,
    SilDeviceNested,
    SilProjectDevice,
    SilProjectDeviceNested,
    MtDriveFile,
} from "server/models"
import {
    SilType,
    ProvenType,
    PublishedUnpublished,
    QidCalculationMethod,
    SilMode,
} from "client/utils"
import {
    ResultDatapointListType,

    LoadQidProductById,
    LoadAllQidModels,
    LoadQidModelById,
    LoadQidModelSafetyFunctionById,
    LoadQidModelOptionById,
    LoadQidDatapointById,
    LoadQidDatapoints,
    PrepareResultDatapoints,
    CalculateQidResultData,

    initSilProjectDevice,
} from "client/store"

import { getSilMode } from "client/pages/sil/formulas"

const CreateOriginQidDevice = (
    qidProductId: number,
    qidModelId: number,
    qidSafetyFunctionId: number,
    qidOptionId: number,
    qidDatapointId: number,
    qidMethod: QidCalculationMethod,
) => {
    let qidProduct: QidProduct = null
    let qidModelList: QidModel[] = null // to calculate datapoint avg
    let qidModel: QidModel = null
    let qidSafetyFunction: QidModelSafetyFunction = null
    let qidOption: QidModelOption = null
    let qidDatapoint: QidDatapoint = null
    let qidDatapointList: ResultDatapointListType = null

    let name: string = ""
    let manufacturer: string = ""
    let description: string = ""
    let logoId: number = null
    let logo: MtDriveFile = null

    let qidModelName: string = null
    let qidSafetyFunctionName: string = null
    let qidOptionName: string = null
    let qidDatapointDate: Date = null

    let type: SilType = TYPE_A
    let lowDemandMode: boolean = false
    let highDemandMode: boolean = false
    let continuousMode: boolean = false
    let mode: SilMode = null
    let logicSolver: boolean = false
    let provenInUse: ProvenType = PROVEN_NO

    let sd: string = ""
    let su: string = ""
    let dd: string = ""
    let du: string = ""
    let status: PublishedUnpublished = UNPUBLISHED


    const promiseArray: Promise<any>[] = []
    if (qidProductId) {
        promiseArray.push(
            LoadQidProductById(qidProductId)
            .then((res) => { qidProduct = res }),
            LoadAllQidModels(qidProductId) // to calculate datapoints avg
            .then((res) => { qidModelList = Object.values(res) }),
            LoadQidDatapoints({ productId: qidProductId })
            .then((res) => { qidDatapointList = PrepareResultDatapoints(res) }),
        )
    }
    if (qidModelId) {
        promiseArray.push(
            LoadQidModelById(qidModelId)
            .then((res) => { qidModel = res }),
        )
    }
    if (qidSafetyFunctionId) {
        promiseArray.push(
            LoadQidModelSafetyFunctionById(qidSafetyFunctionId)
            .then((res) => { qidSafetyFunction = res }),
        )
    }
    if (qidOptionId) {
        promiseArray.push(
            LoadQidModelOptionById(qidOptionId)
            .then((res) => { qidOption = res }),
        )
    }
    if (qidDatapointId) {
        promiseArray.push(
            LoadQidDatapointById(qidDatapointId)
            .then((res) => { qidDatapoint = res }),
        )
    }

    return Promise.all(promiseArray).then(() => {
        if (qidProduct) {
            name = qidProduct.name
            manufacturer = qidProduct.company ? qidProduct.company.name : ""
            description = ""
            logoId = qidProduct.logoId
            logo = qidProduct.logo
            status = qidProduct.status
        }

        qidModelName = qidModel ? qidModel.name : null
        qidSafetyFunctionName = qidSafetyFunction ? qidSafetyFunction.name : null
        qidOptionName = qidOption ? qidOption.name : null
        qidDatapointDate = qidDatapoint ? qidDatapoint.publicationDate : null

        // get data from the lowest level
        if (qidSafetyFunction) {
            type = qidSafetyFunction.type
            lowDemandMode = qidSafetyFunction.lowDemandMode
            highDemandMode = qidSafetyFunction.highDemandMode
            continuousMode = qidSafetyFunction.continuousMode
            mode = getSilMode(qidSafetyFunction)
            logicSolver = qidSafetyFunction.logicSolver
            provenInUse = qidSafetyFunction.provenInUse
        } else if (qidModel) {
            type = qidModel.type
            lowDemandMode = qidModel.lowDemandMode
            highDemandMode = qidModel.highDemandMode
            continuousMode = qidModel.continuousMode
            mode = getSilMode(qidModel)
            logicSolver = qidModel.logicSolver
            provenInUse = qidModel.provenInUse
        } else if (qidProduct) {
            type = qidProduct.type
            lowDemandMode = qidProduct.lowDemandMode
            highDemandMode = qidProduct.highDemandMode
            continuousMode = qidProduct.continuousMode
            mode = getSilMode(qidProduct)
            logicSolver = qidProduct.logicSolver
            provenInUse = qidProduct.provenInUse
        }

        const datapointAvg = CalculateQidResultData({
            models: qidModelList,
            datapointList: qidDatapointList,
            method: qidMethod,
            modelId: qidModelId,
            safetyFunctionId: qidSafetyFunctionId,
            optionId: qidOptionId,
            datapointId: qidDatapointId,
        })

        sd = (datapointAvg && datapointAvg.sd !== null) ? datapointAvg.sd.toString() : null
        su = (datapointAvg && datapointAvg.su !== null) ? datapointAvg.su.toString() : null
        dd = (datapointAvg && datapointAvg.dd !== null) ? datapointAvg.dd.toString() : null
        du = (datapointAvg && datapointAvg.du !== null) ? datapointAvg.du.toString() : null


        const projectDevice: SilProjectDevice = {
            ...initSilProjectDevice,
            name,
            manufacturer,
            description,
            logoId,
            logo,

            qidProductId,
            qidModelId,
            qidSafetyFunctionId,
            qidOptionId,
            qidDatapointId,
            qidMethod,

            qidModelName,
            qidSafetyFunctionName,
            qidOptionName,
            qidDatapointDate,

            type,
            lowDemandMode,
            highDemandMode,
            continuousMode,
            logicSolver,
            provenInUse,

            failureRate: true, // will use calculated SFF and DC
            sff: null,
            dc: null,
            pfd: null,
            pfh: null,
            sil: null,

            sd,
            su,
            dd,
            du,

            status,
        }

        const nestedDevice: SilDeviceNested = {
            id: null,
            parentDeviceId: null,
            deviceId: null,

            qidProductId,
            qidModelId,
            qidSafetyFunctionId,
            qidOptionId,
            qidDatapointId,

            qidModelName,
            qidSafetyFunctionName,
            qidOptionName,
            qidDatapointDate,
            qidMethod,

            manufacturer,
            name,
            logoId,
            logo,
            type,
            mode,
            logicSolver,
            provenInUse,

            sd,
            su,
            dd,
            du,

            order: null,
        }

        const projectNestedDevice: SilProjectDeviceNested = {
            ...nestedDevice,
            projectDeviceId: null,
        }

        // check that we load existing device
        if (
            (qidProductId && qidProduct)
            && ((qidModelId && qidModel) || qidModelId === null)
            && ((qidSafetyFunctionId && qidSafetyFunction) || qidSafetyFunctionId === null)
            && ((qidOptionId && qidOption) || qidOptionId === null)
            && ((qidDatapointId && qidDatapoint) || qidDatapointId === null)
        ) {
            return {
                projectDevice,
                nestedDevice,
                projectNestedDevice,
            }
        }

        // if origin device was removed return "null"
        return {
            projectDevice: null,
            nestedDevice: null,
            projectNestedDevice: null,
        }
    })
}
export default CreateOriginQidDevice
